/**
 * Die Rechnung der Kostenseite „Kosten · offene Zahlungen" - `renderCostPage` (`index.html:13828-13900`),
 * so wie `components/CostsPage/KostenSeite.tsx` sie bis zum KI-Zugang (Stufe 1) im Funktionsrumpf
 * fuehrte. Sie ist UNVERAENDERT in diese Funktion gezogen, weil der Server
 * (`backend/src/ki/auswertung/frontend/`) dieselbe Rechnung fahren muss wie die Oberflaeche und dafuer
 * eine zeichengleiche Kopie dieses Moduls braucht; die Seite rendert nur noch das Ergebnis.
 *
 * Alle Eigenheiten, die die Seite dokumentiert, gelten weiter (Kopfkommentar von `KostenSeite.tsx`):
 * `activeReportingOrders` filtert vor den offenen Posten (1), die Summen entstehen aus getrennten
 * `reduce`-Laeufen ueber verschiedene Teilmengen, nicht aus einer Zwischensumme (2: die
 * Gleitkommareihenfolge bleibt die des Originals), `byOrder` gruppiert ueber `r.order.id`, Bestellungen
 * OHNE `id` landen also in EINEM Balken (3), und der Naeherungshinweis gilt, sobald IRGENDEINE Zeile
 * `approx` ist (4). Die Farbe der Kachel „Ueberfaellig" haengt an der LISTE der ueberfaelligen Zeilen,
 * nicht an ihrer Summe - sie steht als `hatUeberfaellige` im Ergebnis. Die Aufteilung in Vor-/Nach-ETD
 * steht ebenfalls im Ergebnis (`vorZeilen`, `nachZeilen`), die Seite filtert und rechnet nichts mehr selbst;
 * `vorEtd`/`nachEtd`/`ueberfaellig` sind aus diesen Listen summiert, die Phasenregel steht also einmal.
 *
 * Regel fuer dieses Modul (`test/lib/reineKette.test.ts`): nur `.ts`-Importe innerhalb von
 * `frontend/src`, kein `react`, kein `import.meta`, keine Browser-Globals.
 */
import { orderOpenPayments } from "./fx.ts";
import type { FxDeal, OpenPaymentRow } from "./fx.ts";
import { istBestellungNachLaenderFilterSichtbar } from "./laender.ts";
import type { LaenderFilter } from "./laender.ts";
import { activeReportingOrders } from "./status.ts";
import type { StatusDefinitionen } from "./status.ts";
import { istBestellungNachTransportartFilterSichtbar } from "./transportart.ts";
import type { TransportartFilter } from "./transportart.ts";
import type { Order, Supplier } from "./types.ts";

/** Ein Balken: eine Bestellung mit ihren beiden Phasensummen (index.html:13842). */
export interface KostenBalken {
  order: Order;
  vor: number;
  nach: number;
}

export interface KostenEingabe {
  bestellungen: readonly Order[];
  deals: readonly FxDeal[] | null | undefined;
  lieferanten: readonly Supplier[] | null | undefined;
  /** Rueckgabe von `setStatusDefinitions(boardSpalten)`. */
  definitionen: StatusDefinitionen;
  /** Der EU/China-Laenderfilter der Kopfzeile, angewandt auf die aktiven Bestellungen. */
  laenderFilter: LaenderFilter;
  /** Der Transportart-Filter der Kopfzeile, angewandt an derselben Stelle. */
  transportartFilter: TransportartFilter;
}

export interface KostenAuswertung {
  /** Alle offenen Posten (vor und nach ETD) der sichtbaren, aktiven Bestellungen, in Bestellreihenfolge. */
  zeilen: OpenPaymentRow[];
  /** Die Zeilen mit `phase === "vor"` - die Tabelle „Vor ETD faellig", in der Reihenfolge von `zeilen`. */
  vorZeilen: OpenPaymentRow[];
  /** Die Zeilen mit `phase === "nach"` - die Tabelle „Nach ETD faellig", in der Reihenfolge von `zeilen`. */
  nachZeilen: OpenPaymentRow[];
  /**
   * Ob irgendeine Zeile `overdue` ist (Wahrheitswert von `zeilen.some((r) => r.overdue)`). Faerbt die
   * Kachel „Ueberfaellig" rot - NICHT `ueberfaellig > 0`: eine ueberfaellige Zeile mit Betrag 0 faerbt sie auch.
   */
  hatUeberfaellige: boolean;
  /** „Offen gesamt (≈ €)" - Summe aller `zeilen`. */
  offenGesamt: number;
  /** „Vor ETD faellig" - Summe der Zeilen mit `phase === "vor"`. */
  vorEtd: number;
  /** „Nach ETD faellig" - Summe der Zeilen mit `phase === "nach"`. */
  nachEtd: number;
  /** „Ueberfaellig" - Summe der Zeilen mit `overdue`. */
  ueberfaellig: number;
  /** Ob irgendeine Zeile `approx` ist (Bestellung ohne gesicherten Kurs): Naeherungshinweis der Seite. */
  naeherung: boolean;
  /** Die zwoelf groessten Bestellungen nach offenem Betrag, absteigend. */
  balken: KostenBalken[];
}

/** index.html:13833 */
function sumEur(zeilen: readonly OpenPaymentRow[]): number {
  return zeilen.reduce((s, r) => s + r.eur, 0);
}

/**
 * Die Reihenfolge der beiden Phasentabellen: nach Faelligkeit aufsteigend (`PhasenTabelle.tsx`, `phaseTable` index.html:13849-13867). Der
 * Vergleich ist `localeCompare` auf den ISO-Datumstexten, der Rueckfall fuer eine fehlende Faelligkeit die Zeichenkette `'9999'` (sortiert
 * ans Ende). Sortiert eine KOPIE (das Original sortiert in place, Eigenheit 2 des Kopfkommentars von `PhasenTabelle.tsx`). Stand bis zum
 * KI-Zugang (Stufe 1) im Rumpf der Komponente; der Server nennt die Zeilen in derselben Reihenfolge wie die Tabellen.
 */
export function sortiereOffenePosten(zeilen: readonly OpenPaymentRow[]): OpenPaymentRow[] {
  return [...zeilen].sort((a, b) => (a.due || "9999").localeCompare(b.due || "9999"));
}

export function kostenAuswertung(e: KostenEingabe): KostenAuswertung {
  // index.html:13831-13838, danach der Laenderfilter - dieselbe geteilte Regel wie in
  // `BoardSeite.tsx`/`UebersichtSeite.tsx`/`ZahlungsSeite.tsx`.
  const active = activeReportingOrders(e.bestellungen, e.definitionen)
    .filter((order) => istBestellungNachLaenderFilterSichtbar(order, e.lieferanten, e.laenderFilter))
    .filter((order) => istBestellungNachTransportartFilterSichtbar(order, e.transportartFilter));
  const all = active.flatMap((o) => orderOpenPayments(o, e.deals, e.lieferanten));
  const openTotal = sumEur(all);
  const vor = all.filter((r) => r.phase === "vor");
  const nach = all.filter((r) => r.phase === "nach");
  const overdue = all.filter((r) => r.overdue);
  const anyApprox = all.some((r) => r.approx);

  // index.html:13840-13846 - Balken je Bestellung (offene €-Naeherung), gestapelt vor/nach ETD
  const byOrder = new Map<unknown, KostenBalken>();
  for (const r of all) {
    const b = byOrder.get(r.order.id) || { order: r.order, vor: 0, nach: 0 };
    // Der Index-Zugriff ist im Original `b[r.phase] += r.eur`; `phase` ist dort per Konstruktion
    // immer `'vor'` oder `'nach'` (index.html:13808). Die Einengung macht daraus reine Typarbeit
    // ohne Laufzeitwirkung - ein anderer Wert kaeme aus `orderOpenPayments` nicht heraus.
    b[r.phase as "vor" | "nach"] += r.eur;
    byOrder.set(r.order.id, b);
  }
  const bars = [...byOrder.values()]
    .sort((a, b) => b.vor + b.nach - (a.vor + a.nach))
    .slice(0, 12);

  return {
    zeilen: all,
    vorZeilen: vor,
    nachZeilen: nach,
    // Eigenheit 1: die Farbe der Kachel „Ueberfaellig" haengt an der LISTE, nicht an der Summe - eine
    // ueberfaellige Zeile mit Betrag 0 faerbt sie ebenfalls (nicht `ueberfaellig > 0`).
    hatUeberfaellige: overdue.length > 0,
    offenGesamt: openTotal,
    vorEtd: sumEur(vor),
    nachEtd: sumEur(nach),
    ueberfaellig: sumEur(overdue),
    naeherung: anyApprox,
    balken: bars,
  };
}
